import { apiService } from '../../../components/api/apiService';
import { logDataModels } from './dataModelsLogger';

const CONNECTION = 'backend_py';
const BASE = '/api/v1/reporting/data-models/query-logs';

export type QueryLogKind = 'preview' | 'query' | 'governed' | 'file' | string;

export interface WorkspaceQueryLog {
  eventId: string;
  actorId: string;
  actorName: string;
  actorEmail: string;
  occurredAt: string;
  workspaceId: string;
  workspaceName: string;
  catalogId: string;
  catalogName: string;
  assetId: string;
  assetName: string;
  assetKind: string;
  api: string;
  method: string;
  kind: QueryLogKind;
  outcome: string;
  statusCode: number;
  durationMs: number;
  rowCount: number | null;
  summary: string;
  detail: Record<string, unknown>;
}

export interface QueryLogQuery {
  workspaceId: string;
  catalogId?: string;
  assetId?: string;
  q?: string;
  outcome?: string;
  kind?: string;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
}

export interface QueryLogPage {
  items: WorkspaceQueryLog[];
  nextCursor: string | null;
}

export interface QueryLogMetricPoint {
  key: string;
  label: string;
  hits: number;
}

export interface QueryLogMetrics {
  hits: number;
  users: number;
  assets: number;
  avgDurationMs: number;
  succeeded: number;
  denied: number;
  failed: number;
  byDay: QueryLogMetricPoint[];
  byOutcome: QueryLogMetricPoint[];
  byUser: QueryLogMetricPoint[];
  byAsset: QueryLogMetricPoint[];
  byKind: QueryLogMetricPoint[];
}

function asMetricPoint(row: Partial<QueryLogMetricPoint> | null | undefined): QueryLogMetricPoint | null {
  if (!row || (!row.key && !row.label)) return null;
  return { key: row.key || row.label || '', label: row.label || row.key || '', hits: Number(row.hits || 0) };
}

function asMetrics(row: Partial<QueryLogMetrics> | null | undefined): QueryLogMetrics {
  const points = (value: unknown) =>
    Array.isArray(value)
      ? value.map((item) => asMetricPoint(item as Partial<QueryLogMetricPoint>)).filter((item): item is QueryLogMetricPoint => Boolean(item))
      : [];
  return {
    hits: Number(row?.hits || 0),
    users: Number(row?.users || 0),
    assets: Number(row?.assets || 0),
    avgDurationMs: Number(row?.avgDurationMs || 0),
    succeeded: Number(row?.succeeded || 0),
    denied: Number(row?.denied || 0),
    failed: Number(row?.failed || 0),
    byDay: points(row?.byDay),
    byOutcome: points(row?.byOutcome),
    byUser: points(row?.byUser),
    byAsset: points(row?.byAsset),
    byKind: points(row?.byKind),
  };
}

function serviceErrorMessage(err: unknown, fallback: string): string {
  const axiosErr = err as {
    response?: { data?: { error?: { message?: string }; message?: string } };
    message?: string;
  };
  return axiosErr.response?.data?.error?.message || axiosErr.response?.data?.message || axiosErr.message || fallback;
}

function resolveBackendUrl(): string {
  return (process.env.REACT_APP_API_URL_backend_py || process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
}

function asParams(query: QueryLogQuery): Record<string, string | number> {
  const params: Record<string, string | number> = { workspaceId: query.workspaceId };
  if (query.catalogId) params.catalogId = query.catalogId;
  if (query.assetId) params.assetId = query.assetId;
  if (query.q) params.q = query.q;
  if (query.outcome) params.outcome = query.outcome;
  if (query.kind) params.kind = query.kind;
  if (query.from) params.from = query.from;
  if (query.to) params.to = query.to;
  if (query.cursor) params.cursor = query.cursor;
  if (query.limit) params.limit = query.limit;
  return params;
}

export function asQueryLog(row: Partial<WorkspaceQueryLog>): WorkspaceQueryLog | null {
  if (!row?.eventId) return null;
  return {
    eventId: row.eventId,
    actorId: row.actorId || '',
    actorName: row.actorName || '',
    actorEmail: row.actorEmail || '',
    occurredAt: row.occurredAt || '',
    workspaceId: row.workspaceId || '',
    workspaceName: row.workspaceName || '',
    catalogId: row.catalogId || '',
    catalogName: row.catalogName || '',
    assetId: row.assetId || '',
    assetName: row.assetName || '',
    assetKind: row.assetKind || '',
    api: row.api || '',
    method: row.method || 'POST',
    kind: row.kind || '',
    outcome: row.outcome || 'succeeded',
    statusCode: Number(row.statusCode || 0),
    durationMs: Number(row.durationMs || 0),
    rowCount: row.rowCount == null || row.rowCount === undefined ? null : Number(row.rowCount),
    summary: row.summary || '',
    detail: row.detail && typeof row.detail === 'object' ? row.detail : {},
  };
}

export async function listWorkspaceQueryLogs(query: QueryLogQuery): Promise<QueryLogPage> {
  logDataModels('app', 'workspace_query_logs_load', {
    workspaceId: query.workspaceId,
    catalogId: query.catalogId,
    assetId: query.assetId,
    from: query.from,
    to: query.to,
  });
  try {
    const data = (await apiService.get(BASE, asParams(query), CONNECTION)) as { items?: unknown; nextCursor?: string | null };
    const items = Array.isArray(data?.items)
      ? data.items.map((item) => asQueryLog(item as Partial<WorkspaceQueryLog>)).filter((item): item is WorkspaceQueryLog => Boolean(item))
      : [];
    logDataModels('app', 'workspace_query_logs_load_ok', { count: items.length, hasMore: Boolean(data?.nextCursor) });
    return { items, nextCursor: data?.nextCursor || null };
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to load query logs');
    logDataModels('error', 'workspace_query_logs_load_failed', { message });
    throw new Error(message);
  }
}

export async function fetchWorkspaceQueryLogMetrics(query: QueryLogQuery): Promise<QueryLogMetrics> {
  logDataModels('app', 'workspace_query_logs_metrics_load', {
    workspaceId: query.workspaceId,
    catalogId: query.catalogId,
    assetId: query.assetId,
    from: query.from,
    to: query.to,
  });
  try {
    const data = (await apiService.get(`${BASE}/metrics`, asParams(query), CONNECTION)) as Partial<QueryLogMetrics>;
    const metrics = asMetrics(data);
    logDataModels('app', 'workspace_query_logs_metrics_load_ok', { hits: metrics.hits, users: metrics.users });
    return metrics;
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to load query metrics');
    logDataModels('error', 'workspace_query_logs_metrics_load_failed', { message });
    throw new Error(message);
  }
}

export async function clearWorkspaceQueryLogs(
  workspaceId: string,
  scope?: { catalogId?: string; assetId?: string },
): Promise<number> {
  logDataModels('app', 'workspace_query_logs_clear', { workspaceId, catalogId: scope?.catalogId, assetId: scope?.assetId });
  try {
    const params = new URLSearchParams({ workspaceId });
    if (scope?.catalogId) params.set('catalogId', scope.catalogId);
    if (scope?.assetId) params.set('assetId', scope.assetId);
    const data = (await apiService.delete(`${BASE}?${params.toString()}`, CONNECTION)) as { deleted?: number };
    const deleted = Number(data?.deleted || 0);
    logDataModels('app', 'workspace_query_logs_clear_ok', { workspaceId, deleted });
    return deleted;
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to clear query logs');
    logDataModels('error', 'workspace_query_logs_clear_failed', { message });
    throw new Error(message);
  }
}

export function openWorkspaceQueryLogLive(
  workspaceId: string,
  onEvent: (event: WorkspaceQueryLog) => void,
  signal: AbortSignal,
  scope?: { catalogId?: string; assetId?: string },
): Promise<void> {
  const params = new URLSearchParams({ workspaceId });
  if (scope?.catalogId) params.set('catalogId', scope.catalogId);
  if (scope?.assetId) params.set('assetId', scope.assetId);
  const url = `${resolveBackendUrl()}${BASE}/live?${params.toString()}`;
  logDataModels('app', 'workspace_query_logs_live_start', {
    workspaceId,
    catalogId: scope?.catalogId,
    assetId: scope?.assetId,
  });
  return fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'text/event-stream',
      Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
    },
    signal,
  }).then(async (response) => {
    if (!response.ok || !response.body) {
      const message = `Live debug failed (${response.status})`;
      logDataModels('error', 'workspace_query_logs_live_failed', { message, status: response.status });
      throw new Error(message);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    try {
      while (!signal.aborted) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() || '';
        for (const chunk of chunks) {
          const dataLine = chunk
            .split('\n')
            .map((line) => line.trimEnd())
            .find((line) => line.startsWith('data:'));
          if (!dataLine) continue;
          try {
            const parsed = JSON.parse(dataLine.slice(5).trim()) as Partial<WorkspaceQueryLog>;
            const event = asQueryLog(parsed);
            if (event) onEvent(event);
          } catch {
            logDataModels('warning', 'workspace_query_logs_live_parse_failed', { workspaceId });
          }
        }
      }
      logDataModels('app', 'workspace_query_logs_live_stop', { workspaceId });
    } finally {
      try {
        await reader.cancel();
      } catch {
        /* ignore */
      }
    }
  });
}
